import { softmax } from './math';

export const EMOTION_DIMENSIONS = [
  { key: 'valence', label: 'Valence', min: -1, max: 1, defaultValue: 0, calibration: 'tanh' },
  { key: 'arousal', label: 'Arousal', min: 0, max: 1, defaultValue: 0.4 },
  { key: 'dominance', label: 'Dominance', min: 0, max: 1, defaultValue: 0.5 },
  { key: 'energy', label: 'Energy', min: 0, max: 1, defaultValue: 0.5 },
  { key: 'focus', label: 'Focus', min: 0, max: 1, defaultValue: 0.5 },
  { key: 'social', label: 'Social Need', min: 0, max: 1, defaultValue: 0.4 },
  { key: 'novelty', label: 'Novelty Seeking', min: 0, max: 1, defaultValue: 0.45 },
  { key: 'stress', label: 'Stress', min: 0, max: 1, defaultValue: 0.2 }
];

export const DIMENSION_LOOKUP = EMOTION_DIMENSIONS.reduce((acc, dim, index) => {
  acc[dim.key] = { ...dim, index };
  return acc;
}, {});

export const DEFAULT_DECAY = EMOTION_DIMENSIONS.map((dim) => (dim.key === 'valence' ? 0.9 : 0.85));

export const DEFAULT_B_MATRIX = [
  [0.25, 0.05, 0, 0, 0, 0, 0, -0.05],
  [0.05, 0.3, 0.05, 0.05, 0.05, 0.02, 0.02, 0.1],
  [0, 0.05, 0.3, 0.1, 0.05, 0.02, 0.02, 0.08],
  [0.05, 0.08, 0.1, 0.35, 0.05, 0.05, 0.05, 0.1],
  [0.05, 0.08, 0.08, 0.05, 0.35, 0.05, 0.02, 0.02],
  [0.08, 0.04, 0.02, 0.02, 0.05, 0.3, 0.05, 0.05],
  [0.08, 0.04, 0.04, 0.05, 0.03, 0.05, 0.32, 0.05],
  [-0.05, 0.12, 0.12, 0.05, 0.02, 0.08, 0.05, 0.35]
];

export const DEFAULT_SPEED_LIMIT = 0.35;
export const DEFAULT_ALPHA = 0.35;
export const DEFAULT_TEMPERATURE = 0.7;

export const PROTOTYPES = {
  happiness: [0.85, 0.55, 0.6, 0.65, 0.55, 0.6, 0.55, 0.15],
  calm: [0.55, 0.25, 0.6, 0.35, 0.6, 0.35, 0.35, 0.1],
  hope: [0.65, 0.45, 0.55, 0.55, 0.6, 0.45, 0.6, 0.2],
  curiosity: [0.6, 0.6, 0.55, 0.5, 0.65, 0.45, 0.75, 0.25],
  pride: [0.75, 0.6, 0.75, 0.6, 0.65, 0.55, 0.5, 0.2],
  focus: [0.4, 0.5, 0.55, 0.45, 0.8, 0.35, 0.3, 0.25],
  fatigue: [-0.2, 0.25, 0.3, 0.2, 0.25, 0.15, 0.2, 0.35],
  boredom: [-0.1, 0.2, 0.3, 0.25, 0.25, 0.25, 0.2, 0.2],
  stress: [-0.4, 0.7, 0.35, 0.65, 0.4, 0.35, 0.25, 0.75],
  anger: [-0.7, 0.75, 0.35, 0.6, 0.35, 0.25, 0.2, 0.7],
  sadness: [-0.6, 0.35, 0.3, 0.25, 0.3, 0.2, 0.2, 0.55],
  loneliness: [-0.3, 0.25, 0.35, 0.25, 0.3, 0.15, 0.2, 0.5]
};

export function createEmotionState(overrides = {}) {
  const state = {};
  EMOTION_DIMENSIONS.forEach((dim) => {
    state[dim.key] = clampValue(
      overrides[dim.key] ?? dim.defaultValue,
      dim.min,
      dim.max
    );
  });
  return state;
}

export function stateToVector(state) {
  return EMOTION_DIMENSIONS.map((dim) => clampValue(state?.[dim.key] ?? dim.defaultValue, dim.min, dim.max));
}

export function vectorToState(vector = []) {
  const state = {};
  EMOTION_DIMENSIONS.forEach((dim, index) => {
    state[dim.key] = clampValue(vector[index] ?? dim.defaultValue, dim.min, dim.max);
  });
  return state;
}

export function clampValue(value, min, max) {
  if (Number.isNaN(value) || value === undefined || value === null) return min;
  return Math.max(min, Math.min(max, value));
}

export function clampVector(vector) {
  return stateToVector(vectorToState(vector));
}

export function blendCircadianState({ circadianMatrix, hour, shortTermState, alpha = DEFAULT_ALPHA }) {
  const hourIndex = Number.isInteger(hour) ? ((hour % 24) + 24) % 24 : new Date().getHours();
  const circadianVector = circadianMatrix?.[hourIndex] || EMOTION_DIMENSIONS.map((dim) => dim.defaultValue);
  const circadianState = stateToVector(vectorToState(circadianVector));
  const shortTermVector = stateToVector(shortTermState);
  const blended = circadianState.map((value, index) => {
    return alpha * value + (1 - alpha) * shortTermVector[index];
  });
  return vectorToState(blended);
}

export function updateShortTermState({
  previous,
  signal,
  A = DEFAULT_DECAY,
  B = DEFAULT_B_MATRIX,
  noise = 0,
  speedLimit = DEFAULT_SPEED_LIMIT
}) {
  const prevVector = stateToVector(previous);
  const signalVector = Array.isArray(signal) ? signal : stateToVector(signal);

  const diagonalApplied = prevVector.map((value, index) => value * (A[index] ?? DEFAULT_DECAY[index] ?? 0.85));
  const transformedSignal = multiplyMatrixVector(B, signalVector);
  const nextVector = diagonalApplied.map((value, index) => value + transformedSignal[index] + noise);
  const clamped = stateToVector(vectorToState(nextVector));

  const delta = clamped.map((value, index) => value - prevVector[index]);
  const magnitude = Math.sqrt(delta.reduce((acc, value) => acc + value * value, 0));
  if (magnitude > speedLimit && magnitude > 0) {
    const scale = speedLimit / magnitude;
    const limited = prevVector.map((value, index) => value + delta[index] * scale);
    return vectorToState(limited);
  }
  return vectorToState(clamped);
}

export function projectToPrototypeMix(stateVector, temperature = DEFAULT_TEMPERATURE) {
  const vector = stateToVector(stateVector);
  const distances = Object.entries(PROTOTYPES).map(([label, proto]) => {
    const dist = euclideanSquared(vector, proto);
    return { label, distance: dist };
  });
  const weights = softmax(distances.map((item) => -item.distance / Math.max(temperature, 1e-3)));
  return distances.map((item, index) => ({ label: item.label, weight: weights[index] })).sort((a, b) => b.weight - a.weight);
}

export function euclideanSquared(a, b) {
  const length = Math.max(a.length, b.length);
  let sum = 0;
  for (let i = 0; i < length; i += 1) {
    const diff = (a[i] ?? 0) - (b[i] ?? 0);
    sum += diff * diff;
  }
  return sum;
}

export function multiplyMatrixVector(matrix, vector) {
  if (!Array.isArray(matrix) || !Array.isArray(vector)) return Array(vector?.length || EMOTION_DIMENSIONS.length).fill(0);
  return matrix.map((row) => row.reduce((acc, value, index) => acc + value * (vector[index] ?? 0), 0));
}

export function createDefaultCircadianMatrix(baseState = createEmotionState()) {
  const baseVector = stateToVector(baseState);
  const matrix = [];
  for (let hour = 0; hour < 24; hour += 1) {
    const modulation = Math.sin((Math.PI * hour) / 12);
    const entry = baseVector.map((value, index) => {
      const dim = EMOTION_DIMENSIONS[index];
      if (dim.key === 'energy') return clampValue(value + modulation * 0.1, dim.min, dim.max);
      if (dim.key === 'focus') return clampValue(value + modulation * 0.08, dim.min, dim.max);
      if (dim.key === 'stress') return clampValue(value - modulation * 0.05, dim.min, dim.max);
      return value;
    });
    matrix.push(entry);
  }
  return matrix;
}

export function describeState(state) {
  const mix = projectToPrototypeMix(state);
  const top = mix.slice(0, 3);
  return top.map((item) => `${Math.round(item.weight * 100)}% ${item.label}`).join(', ');
}

const emotionSpace = {
  EMOTION_DIMENSIONS,
  PROTOTYPES,
  createEmotionState,
  stateToVector,
  vectorToState,
  blendCircadianState,
  updateShortTermState,
  projectToPrototypeMix,
  createDefaultCircadianMatrix,
  describeState
};

export default emotionSpace;

